/* The ONE form (designs/intake-unified-form.md UF4, UF13–UF16): register a
   large effort in plain words. Team is pick-or-add, the point of contact
   is a person from the roster, similar efforts surface WHILE typing, and
   nothing the requester never answered is defaulted — priority, phase and
   Jama status are groom-side or not recorded. The submit-time dedup
   interrupt stays as the last guard (HR-A4). */
import { useEffect, useId, useRef, useState, type JSX } from "react";
import {
  SCOPES,
  validateCapabilityInput,
  type Capability,
  type CapabilityInput,
  type PersonRef,
} from "../../../shared/capabilities";
import type { CanonicalTeam } from "../../../shared/canonical-teams";
import type { Product, Vehicle } from "../../../shared/products";
import { asJson } from "./api";
import { PersonPicker } from "./PersonPicker";
import { ProductPicker } from "./ProductPicker";
import { VehiclePicker } from "./VehiclePicker";
import { TeamPicker } from "./TeamPicker";
import { Select } from "./fields";
import { BUTTON_HELP, FORM_HELP, FORM_INTRO, FORM_MISSING, SCOPE_HELP, type FormField } from "./form-help";
import { INTAKE_ABOUT_MODULE } from "../../../shared/intake-about-module";

interface Similar {
  id: string;
  name: string;
  teamId: string;
  description: string | null;
  stakeholderCount: number;
  score: number;
}

/* live matching (UF4): the same /similar the submit uses, debounced so a
   keystroke is not a request */
const LIVE_DEBOUNCE_MS = 350;

export function CaptureForm({
  onRecorded,
  onAbout,
}: {
  /* queue flow (intake-dashboard.md ID1): a recorded submission hands
     control back instead of showing the internal "Recorded" state */
  onRecorded?: (capability: Capability) => void;
  /* the page head's "How intake works →" (intake-form-help.md FH2) */
  onAbout?: () => void;
}): JSX.Element {
  /* a plain field's hint, tied to its input (FH4) */
  const hintBase = useId();
  const hintId = (k: FormField): string => `${hintBase}-${k}`;
  const hint = (k: FormField): JSX.Element => (
    <small className="capture-hint" id={hintId(k)}>
      {FORM_HELP[k].hint}
    </small>
  );
  const [teams, setTeams] = useState<CanonicalTeam[] | null>(null);
  const [teamsError, setTeamsError] = useState(false);
  const [teamId, setTeamId] = useState("");
  /* the product list (UF21): optional on an effort; an empty list is an
     honest start, the form grows it */
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState("");
  /* the product's vehicle (teams-and-products.md TP4) — optional, and
     cleared whenever the product changes, so it can never name a vehicle
     of another product */
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pointOfContact, setPointOfContact] = useState<PersonRef | null>(null);
  /* "Not sure" is the vocabulary's own honest unknown, so opening on it is
     not a guess */
  const [scope, setScope] = useState("unsure");
  /* UF20 (Trevor): ONE question replaces the status radios and the
     expected-owners list — is anyone already working on this, and which
     team? "In progress by X" records subStatus in_progress + owners [X];
     "not in progress" records subStatus needed + owners not recorded. */
  const [inProgress, setInProgress] = useState(false);
  const [workingTeamId, setWorkingTeamId] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [jamaLink, setJamaLink] = useState("");
  const [live, setLive] = useState<Similar[]>([]);
  const [similar, setSimilar] = useState<Similar[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<Capability | null>(null);
  const [busy, setBusy] = useState(false);
  const liveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetch("/api/intake/teams")
      .then((res) => asJson<CanonicalTeam[]>(res))
      .then(setTeams)
      .catch(() => setTeamsError(true));
    fetch("/api/intake/products")
      .then((res) => asJson<Product[]>(res))
      .then(setProducts)
      .catch(() => setProducts([]));
    fetch("/api/intake/vehicles")
      .then((res) => asJson<Vehicle[]>(res))
      .then(setVehicles)
      .catch(() => setVehicles([]));
  }, []);

  /* similar efforts as you type (UF4) — name carries the most weight in
     the jaccard, so it is the trigger; description sharpens it */
  useEffect(() => {
    if (liveTimer.current !== null) clearTimeout(liveTimer.current);
    if (name.trim().length < 3) {
      setLive([]);
      return;
    }
    liveTimer.current = setTimeout(() => {
      fetch(
        `/api/intake/capabilities/similar?name=${encodeURIComponent(name)}&description=${encodeURIComponent(description)}`,
      )
        .then((res) => asJson<Similar[]>(res))
        .then(setLive)
        .catch(() => setLive([]));
    }, LIVE_DEBOUNCE_MS);
    return () => {
      if (liveTimer.current !== null) clearTimeout(liveTimer.current);
    };
  }, [name, description]);

  /* IC6: a form whose every submit would 400 is not offered */
  if (teamsError)
    return (
      <div className="area-note">
        <p>
          <b>The team list did not load</b> — the form needs it to say whose effort this is.
          Refresh to retry.
        </p>
      </div>
    );
  if (teams === null) return <p className="capture-loading">Loading the team list…</p>;

  const input = (): CapabilityInput => ({
    teamId,
    name,
    description: description.trim() === "" ? null : description.trim(),
    pointOfContact,
    productId: productId === "" ? null : productId,
    vehicleId: productId === "" || vehicleId === "" ? null : vehicleId,
    scope,
    subStatus: inProgress ? "in_progress" : "needed",
    /* groom-side (UF13) and not asked (UF14): honestly not recorded */
    priority: null,
    neededBy: neededBy === "" ? null : neededBy,
    phaseTarget: null,
    jamaStatus: null,
    jamaLink: jamaLink.trim() === "" ? null : jamaLink.trim(),
    dependencies: null,
    /* the working team is a SUGGESTION until a platform manager grooms it
       (UF6); nobody working = not recorded, never an empty guess */
    owners: inProgress && workingTeamId !== "" ? [workingTeamId] : null,
  });

  /* IC5: the client-only pre-checks name what the shared reason would
     garble; everything else is the shared validator verbatim */
  const reasonFor = (candidate: CapabilityInput): string | null => {
    if (!candidate.teamId) return "pick a team";
    if (candidate.pointOfContact === null) return "pick a point of contact";
    if (inProgress && workingTeamId === "") return "say which team is working on it";
    return validateCapabilityInput(candidate, teams, { requireContact: true });
  };

  const post = async (): Promise<void> => {
    const created = await asJson<Capability>(
      await fetch("/api/intake/capabilities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        /* `notDuplicate` when the submitter has SEEN the candidates and
           chose to go on. The interrupt is now enforced server-side for
           every client (designs/claude-surface.md CS6), so this form's
           own check became a pre-flight of the same rule rather than the
           only guard — and without this flag the second click would be
           refused by the API it just satisfied. `similar !== null` is
           exactly "the panel was shown".

           Sent ONLY when it is true: a clean submit posts the record shape
           and nothing else, so the body stays exactly what is stored and
           the flag reads as an assertion somebody made rather than a field
           that is always there. */
        body: JSON.stringify(
          similar === null ? input() : { ...input(), notDuplicate: true },
        ),
      }),
    );
    setSimilar(null);
    if (onRecorded) onRecorded(created);
    else setSubmitted(created);
  };

  /* IC4: the dedup check interrupts the submit, once. One global pool (UF4). */
  const submit = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    const reason = reasonFor(input());
    if (reason) return setError(reason);
    setBusy(true);
    try {
      if (similar === null) {
        const found = await asJson<Similar[]>(
          await fetch(
            `/api/intake/capabilities/similar?name=${encodeURIComponent(name)}&description=${encodeURIComponent(description)}`,
          ),
        );
        if (found.length > 0) {
          setSimilar(found);
          return;
        }
      }
      await post();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const joinInstead = async (candidate: Similar): Promise<void> => {
    if (!teamId) return setError("pick a team first — the +1 is your team's");
    setError(null);
    setBusy(true);
    try {
      await asJson(
        await fetch(`/api/intake/capabilities/${candidate.id}/stakeholders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ teamId }),
        }),
      );
      setSimilar(null);
      setNotice(`Joined "${candidate.name}" as a stakeholder — your team now counts toward its reach.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const teamName = (id: string): string => teams.find((t) => t.id === id)?.name ?? id;

  const candidates = (list: Similar[], tag: string): JSX.Element => (
    <div className="capture-similar" data-similar-panel={tag}>
      <p className="capture-similar-lead">
        <b>Similar efforts already on the register</b> — join one as a stakeholder instead of
        filing a second copy (your +1 raises its reach):
      </p>
      {list.map((candidate) => (
        <div key={candidate.id} className="capture-candidate">
          <div>
            <b>{candidate.name}</b>{" "}
            <span className="capture-candidate-meta">
              {teamName(candidate.teamId)} · {candidate.stakeholderCount} stakeholder
              {candidate.stakeholderCount === 1 ? "" : "s"} · match {candidate.score.toFixed(2)}
            </span>
            {candidate.description !== null && (
              <p className="capture-candidate-desc">{candidate.description}</p>
            )}
          </div>
          <button
            type="button"
            disabled={busy}
            title={BUTTON_HELP.plusOne}
            onClick={() => void joinInstead(candidate)}
          >
            +1 instead
          </button>
        </div>
      ))}
    </div>
  );

  if (submitted)
    return (
      <div className="area-note" data-capture-done>
        <p>
          <b>Recorded:</b> “{submitted.name}” for {teamName(submitted.teamId)}. The original is
          revision 1 — it survives every later edit.
        </p>
        <p>
          <button className="capture-submit" onClick={() => setSubmitted(null)}>
            Register another
          </button>
        </p>
      </div>
    );

  return (
    <>
      <div className="page-head">
        <h1>Register an effort</h1>
        <p>
          {FORM_INTRO}{" "}
          {onAbout !== undefined && (
            <button type="button" className="link-inline" onClick={onAbout}>
              How intake works →
            </button>
          )}
        </p>
        {/* FH3: the stages come from the About record itself, so the two
            pages cannot disagree about the process */}
        <details className="concept" data-form-process>
          <summary>
            <span className="concept-i">ⓘ</span> What happens after you register
          </summary>
          <div className="concept-body">
            {/* the titles carry their own "1 —" numbering */}
            <ul>
              {INTAKE_ABOUT_MODULE.lifecycle.map((s) => (
                <li key={s.key}>{s.title}</li>
              ))}
            </ul>
            {onAbout !== undefined && (
              <button type="button" className="concept-more" onClick={onAbout}>
                The whole process in About intake →
              </button>
            )}
          </div>
        </details>
      </div>
    <form
      className="capture"
      data-capture-form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="capture-grid">
        <TeamPicker
          label="Team"
          teams={teams}
          value={teamId}
          onChange={setTeamId}
          onAdded={(t) => setTeams((prev) => (prev === null ? [t] : [...prev, t]))}
          disabled={busy}
          help={FORM_HELP.team}
        />
        <ProductPicker
          label="Product (optional)"
          products={products}
          teams={teams}
          value={productId}
          onChange={(id) => {
            setProductId(id);
            setVehicleId("");
          }}
          disabled={busy}
          help={FORM_HELP.product}
        />
        {productId !== "" && (
          <VehiclePicker
            label="Vehicle (optional)"
            productId={productId}
            vehicles={vehicles}
            value={vehicleId}
            onChange={setVehicleId}
            onAdded={(v) => setVehicles((prev) => [...prev, v])}
            disabled={busy}
            help={FORM_HELP.vehicle}
          />
        )}
        <div className="capture-field capture-wide">
          <label>
            <span title={FORM_HELP.name.more}>Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What is the effort?"
              aria-describedby={hintId("name")}
            />
          </label>
          {hint("name")}
        </div>
        <div className="capture-field capture-wide">
          <label>
            <span title={FORM_HELP.description.more}>What / why</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What breaks, on which vehicle, when. Plain words."
              aria-describedby={hintId("description")}
            />
          </label>
          {hint("description")}
        </div>
        {similar === null && live.length > 0 && (
          <div className="capture-wide">{candidates(live, "live")}</div>
        )}
        <PersonPicker
          label="Point of contact"
          value={pointOfContact}
          onChange={setPointOfContact}
          missing={FORM_MISSING.contact}
          disabled={busy}
          help={FORM_HELP.contact}
        />
        <div className="capture-field capture-wide" data-in-progress>
          <span title={FORM_HELP.inProgress.more}>Is anyone already working on this?</span>
          <div
            className="capture-checks"
            role="radiogroup"
            aria-label="in progress"
            aria-describedby={hintId("inProgress")}
          >
            <label className={!inProgress ? "capture-check on" : "capture-check"}>
              <input type="radio" name="in-progress" checked={!inProgress} onChange={() => setInProgress(false)} />
              Not in progress
            </label>
            <label className={inProgress ? "capture-check on" : "capture-check"}>
              <input type="radio" name="in-progress" checked={inProgress} onChange={() => setInProgress(true)} />
              In progress
            </label>
          </div>
          {inProgress && (
            <TeamPicker
              label="By which team?"
              teams={teams}
              value={workingTeamId}
              onChange={setWorkingTeamId}
              onAdded={(t) => setTeams((prev) => (prev === null ? [t] : [...prev, t]))}
              disabled={busy}
              help={FORM_HELP.workingTeam}
            />
          )}
          {hint("inProgress")}
        </div>
        <div className="capture-field">
          <label>
            <span title={FORM_HELP.neededBy.more}>Needed by (optional)</span>
            <input
              type="date"
              value={neededBy}
              onChange={(e) => setNeededBy(e.target.value)}
              aria-describedby={hintId("neededBy")}
            />
          </label>
          {hint("neededBy")}
        </div>
        <Select
          label="Affects"
          value={scope}
          options={SCOPES}
          onChange={setScope}
          help={FORM_HELP.scope}
          optionTitle={(v) => SCOPE_HELP[v as keyof typeof SCOPE_HELP]}
        />
        <div className="capture-field capture-wide">
          <label>
            <span title={FORM_HELP.jama.more}>Jama link (optional)</span>
            <input
              value={jamaLink}
              onChange={(e) => setJamaLink(e.target.value)}
              placeholder="https://…"
              aria-describedby={hintId("jama")}
            />
          </label>
          {hint("jama")}
        </div>
      </div>

      {similar !== null && (
        <>
          {candidates(similar, "submit")}
          <button type="submit" className="capture-notdup" disabled={busy} title={BUTTON_HELP.notDuplicate}>
            Not a duplicate — register anyway
          </button>
        </>
      )}

      {error !== null && <p className="capture-error">{error}</p>}
      {notice !== null && <p className="capture-notice" data-capture-notice>{notice}</p>}
      {similar === null && (
        <button type="submit" className="capture-submit" disabled={busy} title={BUTTON_HELP.register}>
          {busy ? "Checking…" : "Register the effort"}
        </button>
      )}
    </form>
    </>
  );
}
